/**
 * 3-D orbitable wing viewer (FXC-9009 — "view/make a wing in the Wing section").
 *
 * Renders the REAL multi-element flapped wing, built ENTIRELY client-side with no
 * backend round-trip: the live `HighLiftAirfoil` + trapezoidal planform + flap band
 * are fed to `buildWingShell()` (geometry/wingShell.ts), which lofts each element
 * across the span and performs the manifold boolean cove-pocket cutout
 * `(clean_wing − band_slab) ∪ cove_band`. The viewer renders one `THREE.Mesh` per
 * returned element (wing / vane / flap) and rebuilds live on every airfoil / deploy
 * / planform / band change. This mirrors the verified Python pipeline
 * (app/services/wing/shell_loft.py) so the preview and the Flow360 export agree.
 *
 * Interaction:
 *   - Smooth orbit / pan / zoom via three's `OrbitControls` (enableDamping=true),
 *     driven by a render-on-change RAF loop.
 *   - Clickable eta-station markers along the wing — clicking one calls
 *     `onSelectStation(eta)` so the parent surface opens the 2-D foil editor for
 *     that station (the same selection state the flat SVG planform uses).
 *
 * Design constraints (same pattern as the prior placeholder / FlowVisLayer):
 *   - One `THREE.WebGLRenderer` per mounted instance; everything disposed on unmount.
 *   - Theme colours from the live `HighliftPalette` (re-renders on light/dark toggle).
 *   - The wing elements use the SAME face colours as the 2-D foil tool
 *     (mainFace / vaneFace / flapFace) so 2-D and 3-D read as one design.
 *
 * Axis convention (matches the planform sketch + wingShell):
 *   X = streamwise (chord, downstream) · Y = spanwise (root→tip) · Z = up.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

import type { HighliftPalette } from "../highlift/foil/useHighliftTheme";
import type { HighLiftAirfoil } from "../highlift/geometry";
import { buildWingShell, type WingPlanform3D } from "../highlift/geometry/wingShell";
import type { WingPlanform, SectionSummary } from "../../api/wing";
import { useManifold } from "./useManifold";

// ---------------------------------------------------------------------------
// Planform helpers
// ---------------------------------------------------------------------------

/** Chord at an eta station by linear interpolation between root and tip chords. */
function chordAt(p: WingPlanform, eta: number): number {
  return p.root_chord + (p.tip_chord - p.root_chord) * eta;
}

/** LE-x (streamwise) at eta by linear interpolation. */
function leXAt(p: WingPlanform, eta: number): number {
  return p.root_le_x + (p.tip_le_x - p.root_le_x) * eta;
}

/** Map the API planform (snake_case, root at y_root) to the wingShell planform
 *  (camelCase, root at y=0 — the y_root offset is applied to the scene group). */
function planformTo3D(p: WingPlanform): WingPlanform3D {
  return {
    rootChord: p.root_chord,
    tipChord: p.tip_chord,
    rootLeX: p.root_le_x,
    tipLeX: p.tip_le_x,
    span: p.span,
    dihedralDeg: 0,
    twistTipDeg: 0,
  };
}

/** Visual marker radius scaled to look reasonable relative to the span. */
function markerRadius(p: WingPlanform): number {
  return Math.max(0.02, p.span * 0.02);
}

const HIT_SCALE = 2.6; // invisible hit sphere is MARKER_RADIUS × HIT_SCALE larger
const DEFAULT_BAND: [number, number] = [0.25, 0.7];
const REBUILD_DEBOUNCE_MS = 50;

/** Per-element render colours, pulled from the live foil-tool palette. */
function elementColor(palette: HighliftPalette, name: string): { face: string; edge: string } {
  if (name === "vane") return { face: palette.vaneFace, edge: palette.vaneEdge };
  if (name === "flap") return { face: palette.flapFace, edge: palette.flapEdge };
  return { face: palette.mainFace, edge: palette.mainEdge };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface WingViewer3DProps {
  planform: WingPlanform;
  /** The live foil design (drives the 3-D shell, incl. flap deploy). */
  airfoil: HighLiftAirfoil;
  etaStations: number[];
  sections: SectionSummary[];
  selectedEta: number | null;
  onSelectStation: (eta: number) => void;
  palette: HighliftPalette;
  /** Flap span band (eta) the cove/vane/flap occupy. Default [0.25, 0.70]. */
  band?: [number, number];
  width?: number;
  height?: number;
}

export function WingViewer3D({
  planform,
  airfoil,
  etaStations,
  sections,
  selectedEta,
  onSelectStation,
  palette,
  band,
  width,
  height,
}: WingViewer3DProps) {
  const { manifold, error: manifoldError } = useManifold();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const wingGroupRef = useRef<THREE.Group | null>(null);
  const rafRef = useRef<number | null>(null);
  const needsRenderRef = useRef<boolean>(true);

  // Live scene contents, rebuilt on the relevant dep changes.
  const elementMeshesRef = useRef<THREE.Mesh[]>([]);
  const markersRef = useRef<Array<{ eta: number; visual: THREE.Mesh; hit: THREE.Mesh }>>([]);
  const buildTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [buildError, setBuildError] = useState<string | null>(null);

  const bandLo = band?.[0] ?? DEFAULT_BAND[0];
  const bandHi = band?.[1] ?? DEFAULT_BAND[1];

  // Latest planform (read inside the framing callback without stale closures).
  const planformRef = useRef(planform);
  planformRef.current = planform;
  // True once the user has orbited — stop auto-re-framing on resize after that.
  const userInteractedRef = useRef(false);

  /** Frame the camera so the whole wing fits, accounting for the live viewport
   *  aspect (the panel is short + wide, so a fixed distance would crop). Fits the
   *  planform bounding sphere to whichever of the vertical / horizontal FOV is
   *  tighter, from a fixed 3/4 viewing direction. */
  const frameToPlanform = useCallback(() => {
    const camera = cameraRef.current;
    const controls = controlsRef.current;
    const container = containerRef.current;
    if (!camera || !controls) return;
    const p = planformRef.current;
    const xMin = Math.min(p.root_le_x, p.tip_le_x);
    const xMax = Math.max(p.root_le_x + p.root_chord, p.tip_le_x + p.tip_chord);
    const cx = (xMin + xMax) / 2;
    const cy = p.y_root + p.span / 2;
    const cz = 0;
    const radius = 0.5 * Math.hypot(Math.max(xMax - xMin, 1e-3), Math.max(p.span, 1e-3)) + 0.3;
    const vfov = (camera.fov * Math.PI) / 180;
    const aspect = container && container.clientHeight > 0 ? container.clientWidth / container.clientHeight : camera.aspect || 1.6;
    const hfov = 2 * Math.atan(Math.tan(vfov / 2) * Math.max(aspect, 0.1));
    const dist = (radius / Math.sin(Math.min(vfov, hfov) / 2)) * 1.15;
    const dir = new THREE.Vector3(0.5, -0.85, 0.5).normalize();
    controls.target.set(cx, cy, cz);
    camera.position.set(cx + dir.x * dist, cy + dir.y * dist, cz + dir.z * dist);
    camera.near = Math.max(0.01, dist * 0.01);
    camera.far = dist * 40;
    camera.updateProjectionMatrix();
    controls.update();
    needsRenderRef.current = true;
  }, []);

  // ---------------------------------------------------------------------------
  // Boot / teardown the renderer + controls (once)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    } catch {
      return;
    }
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(window.devicePixelRatio ?? 1);
    rendererRef.current = renderer;

    const scene = new THREE.Scene();
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(40, 1, 0.01, 5000);
    camera.up.set(0, 0, 1); // Z is "up" for the wing frame
    cameraRef.current = camera;

    // Lighting: soft ambient + two directionals so both surfaces read.
    scene.add(new THREE.AmbientLight(0xffffff, 0.65));
    const key = new THREE.DirectionalLight(0xffffff, 1.15);
    key.position.set(0.4, -0.8, 1.0);
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xffffff, 0.5);
    fill.position.set(-0.6, 0.5, 0.6);
    scene.add(fill);

    const group = new THREE.Group();
    scene.add(group);
    wingGroupRef.current = group;

    const controls = new OrbitControls(camera, canvas);
    controls.enableDamping = true;
    controls.dampingFactor = 0.09;
    controls.rotateSpeed = 0.9;
    controls.zoomSpeed = 0.9;
    controls.panSpeed = 0.8;
    controls.screenSpacePanning = true;
    controls.addEventListener("change", () => {
      needsRenderRef.current = true;
    });
    controls.addEventListener("start", () => {
      userInteractedRef.current = true;
    });
    controlsRef.current = controls;

    const animate = () => {
      rafRef.current = requestAnimationFrame(animate);
      const moved = controls.update();
      if (moved || needsRenderRef.current) {
        needsRenderRef.current = false;
        renderer.render(scene, camera);
      }
    };
    rafRef.current = requestAnimationFrame(animate);

    // Dev-only debug handle (stripped from production builds — import.meta.env.DEV
    // is false there). Lets local tooling drive the camera + force a synchronous
    // render for frame capture.
    if (import.meta.env.DEV) {
      (window as unknown as Record<string, unknown>).__wingViewer3D = {
        renderer,
        scene,
        camera,
        controls,
        render: () => renderer.render(scene, camera),
      };
    }

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      if (buildTimerRef.current !== null) clearTimeout(buildTimerRef.current);
      buildTimerRef.current = null;
      controls.dispose();
      // dispose everything we built
      for (const m of elementMeshesRef.current) {
        m.geometry.dispose();
        (m.material as THREE.Material).dispose();
      }
      elementMeshesRef.current = [];
      for (const m of markersRef.current) {
        m.visual.geometry.dispose();
        m.hit.geometry.dispose();
        (m.visual.material as THREE.Material).dispose();
        (m.hit.material as THREE.Material).dispose();
      }
      markersRef.current = [];
      renderer.dispose();
      rendererRef.current = null;
      sceneRef.current = null;
      cameraRef.current = null;
      controlsRef.current = null;
      wingGroupRef.current = null;
      if (import.meta.env.DEV) delete (window as unknown as Record<string, unknown>).__wingViewer3D;
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Frame the camera to the planform (initial + when the planform changes shape).
  // A planform change re-arms auto-framing so the new shape is centred.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    userInteractedRef.current = false;
    frameToPlanform();
    // Only re-frame on planform shape changes (not on every foil edit).
  }, [planform.root_le_x, planform.tip_le_x, planform.root_chord, planform.tip_chord, planform.span, planform.y_root, frameToPlanform]);

  // ---------------------------------------------------------------------------
  // Rebuild the wing element meshes (debounced) on airfoil / planform / band /
  // manifold-ready change.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const scene = sceneRef.current;
    const group = wingGroupRef.current;
    if (!scene || !group || !manifold) return;

    if (buildTimerRef.current !== null) clearTimeout(buildTimerRef.current);
    buildTimerRef.current = setTimeout(() => {
      buildTimerRef.current = null;
      const g = wingGroupRef.current;
      if (!g) return;
      try {
        const shell = buildWingShell(airfoil, planformTo3D(planform), manifold, {
          band: [bandLo, bandHi],
        });

        // Remove + dispose the previous element meshes.
        for (const m of elementMeshesRef.current) {
          g.remove(m);
          m.geometry.dispose();
          (m.material as THREE.Material).dispose();
        }
        elementMeshesRef.current = [];

        for (const el of shell.elements) {
          const geo = new THREE.BufferGeometry();
          geo.setAttribute("position", new THREE.BufferAttribute(el.positions, 3));
          geo.setIndex(new THREE.BufferAttribute(el.indices, 1));
          geo.computeVertexNormals();
          const { face } = elementColor(palette, el.name);
          const mat = new THREE.MeshPhongMaterial({
            color: new THREE.Color(face),
            specular: new THREE.Color(0x111111),
            shininess: 18,
            side: THREE.DoubleSide,
            flatShading: false,
          });
          const mesh = new THREE.Mesh(geo, mat);
          mesh.name = el.name;
          g.add(mesh);
          elementMeshesRef.current.push(mesh);
        }

        // Apply the spanwise root offset to the whole group.
        g.position.set(0, planform.y_root, 0);
        setBuildError(null);
        needsRenderRef.current = true;
      } catch (e) {
        // A non-manifold build (degenerate inputs) throws — surface it, keep last good mesh.
        // eslint-disable-next-line no-console
        console.error("[WingViewer3D] buildWingShell failed:", e);
        setBuildError(e instanceof Error ? e.message : String(e));
      }
    }, REBUILD_DEBOUNCE_MS);

    return () => {
      if (buildTimerRef.current !== null) {
        clearTimeout(buildTimerRef.current);
        buildTimerRef.current = null;
      }
    };
  }, [manifold, airfoil, planform, bandLo, bandHi, palette]);

  // ---------------------------------------------------------------------------
  // Rebuild station markers on etaStations / sections / selectedEta / palette /
  // planform change.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const group = wingGroupRef.current;
    if (!group) return;

    for (const m of markersRef.current) {
      group.remove(m.visual);
      group.remove(m.hit);
      m.visual.geometry.dispose();
      m.hit.geometry.dispose();
      (m.visual.material as THREE.Material).dispose();
      (m.hit.material as THREE.Material).dispose();
    }
    markersRef.current = [];

    const r = markerRadius(planform);
    const sectionByEta = new Map(sections.map((s) => [s.eta, s]));

    for (const eta of etaStations) {
      const chord = chordAt(planform, eta);
      // Local frame (group carries the y_root offset): float just above the
      // upper surface near the leading edge so the marker is clickable.
      const x = leXAt(planform, eta) + chord * 0.05;
      const y = planform.span * eta;
      const z = chord * 0.16;

      const isSelected = selectedEta != null && Math.abs(selectedEta - eta) < 0.0005;
      const hasDesign = sectionByEta.get(eta)?.has_design ?? false;
      const color = isSelected ? palette.primary : hasDesign ? palette.blue : palette.reattach;

      const vGeo = new THREE.SphereGeometry(r, 18, 12);
      const vMat = new THREE.MeshPhongMaterial({
        color: new THREE.Color(color),
        emissive: new THREE.Color(color).multiplyScalar(isSelected ? 0.45 : 0.12),
      });
      const visual = new THREE.Mesh(vGeo, vMat);
      visual.position.set(x, y, z);
      group.add(visual);

      const hGeo = new THREE.SphereGeometry(r * HIT_SCALE, 8, 6);
      const hMat = new THREE.MeshBasicMaterial({ visible: false });
      const hit = new THREE.Mesh(hGeo, hMat);
      hit.position.set(x, y, z);
      hit.userData = { eta };
      group.add(hit);

      markersRef.current.push({ eta, visual, hit });
    }

    needsRenderRef.current = true;
  }, [etaStations, sections, selectedEta, palette, planform]);

  // ---------------------------------------------------------------------------
  // Resize (renderer + camera aspect)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const resize = () => {
      const renderer = rendererRef.current;
      const camera = cameraRef.current;
      const container = containerRef.current;
      if (!renderer || !camera || !container) return;
      const wpx = Math.max(1, Math.floor(width ?? container.clientWidth));
      const hpx = Math.max(1, Math.floor(height ?? container.clientHeight));
      renderer.setSize(wpx, hpx, false);
      camera.aspect = wpx / hpx;
      camera.updateProjectionMatrix();
      // Keep the wing framed as the panel sizes/reflows — until the user orbits.
      if (!userInteractedRef.current) frameToPlanform();
      needsRenderRef.current = true;
    };

    resize();
    if (width !== undefined && height !== undefined) return;
    const container = containerRef.current;
    if (!container) return;
    const obs = new ResizeObserver(resize);
    obs.observe(container);
    return () => obs.disconnect();
  }, [width, height, frameToPlanform]);

  // ---------------------------------------------------------------------------
  // Click-to-select an eta-station marker (works alongside OrbitControls — a
  // near-stationary pointerdown→up is treated as a click).
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let downX = 0;
    let downY = 0;
    const CLICK_THRESHOLD_PX = 5;

    const onPointerDown = (e: PointerEvent) => {
      downX = e.clientX;
      downY = e.clientY;
    };

    const onPointerUp = (e: PointerEvent) => {
      const moved = Math.hypot(e.clientX - downX, e.clientY - downY);
      if (moved > CLICK_THRESHOLD_PX) return;
      const camera = cameraRef.current;
      if (!camera) return;
      const rect = canvas.getBoundingClientRect();
      const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const ny = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(new THREE.Vector2(nx, ny), camera);
      const hits = markersRef.current.map((m) => m.hit);
      const intersections = raycaster.intersectObjects(hits, false);
      if (intersections.length > 0) {
        const eta = (intersections[0]!.object.userData as { eta: number }).eta;
        onSelectStation(eta);
      }
    };

    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointerup", onPointerUp);
    return () => {
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointerup", onPointerUp);
    };
  }, [onSelectStation]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const backdrop = `radial-gradient(circle at 50% 38%, ${palette.surface2}, ${palette.surface} 72%)`;
  const overlay = manifoldError
    ? `3D engine failed to load: ${manifoldError.message}`
    : !manifold
      ? "Loading 3D engine…"
      : buildError
        ? `Build error: ${buildError}`
        : null;

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: width !== undefined ? width : "100%",
        height: height !== undefined ? height : "100%",
        minHeight: 0,
        overflow: "hidden",
        background: backdrop,
        borderRadius: 8,
        cursor: "grab",
      }}
    >
      <canvas
        ref={canvasRef}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" }}
      />
      {overlay ? (
        <div
          style={{
            position: "absolute",
            top: 10,
            left: 10,
            fontSize: 11,
            color: manifoldError || buildError ? palette.warn : palette.ink3,
            pointerEvents: "none",
            fontFamily: "'JetBrains Mono', monospace",
            background: palette.surface,
            border: `1px solid ${palette.line}`,
            borderRadius: 6,
            padding: "4px 8px",
            maxWidth: "70%",
          }}
        >
          {overlay}
        </div>
      ) : null}
      <div
        style={{
          position: "absolute",
          bottom: 8,
          left: 10,
          fontSize: 10,
          color: palette.ink4,
          pointerEvents: "none",
          fontFamily: "'JetBrains Mono', monospace",
          lineHeight: 1.4,
        }}
      >
        drag to orbit · scroll to zoom · click a marker to open 2D foil
      </div>
    </div>
  );
}
